import classNames from 'classnames';
import React, { forwardRef, useRef } from 'react';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useScrollTrigger } from '~/hooks/useScrollTrigger';
import {
  Desktop as MainNavDesktop,
  MainNavMobile,
} from '~/layouts/nav/MainNav';

type NavContainerProps = {
  /** The navigation content to render */
  nav: React.ReactNode;
  /** The main content to render */
  children: React.ReactNode;
  /** Whether to enable scroll detection */
  enableScrollDetection?: boolean;
};

type FilterRowProps = {
  /** The content of the filter row */
  children: React.ReactNode;
  /** Additional class names to apply to the filter row */
  className?: string;
};

export function NavContainer({
  nav,
  children,
  enableScrollDetection = true,
}: NavContainerProps) {
  const navRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();

  // Define a scroll threshold for mobile
  const MOBILE_SCROLL_THRESHOLD = 150;

  // Use the scroll trigger hook
  const isScrollingDown = useScrollTrigger({
    threshold: MOBILE_SCROLL_THRESHOLD,
    enabled: enableScrollDetection && isMobile,
  });

  return (
    <>
      {/* Top header (desktop nav + filters/content). This hides upward on scroll. */}
      <div
        ref={navRef}
        className={classNames(
          'fixed top-0 z-50 w-full bg-primary-container transition-transform duration-300 ease-in-out lg:sticky lg:bg-white',
          { '-translate-y-full': isScrollingDown }
        )}
      >
        <MainNavDesktop />

        <div className='w-full border-outline-bright bg-primary-container transition-transform duration-300 ease-in-out lg:static lg:z-auto lg:w-auto lg:border-b lg:bg-white'>
          {nav}
          {children}
        </div>
      </div>

      {/* Bottom mobile nav. Render outside the transformed header so it slides down independently. */}
      {isMobile && (
        <MainNavMobile
          enableScrollDetection={enableScrollDetection}
          threshold={MOBILE_SCROLL_THRESHOLD}
        />
      )}
    </>
  );
}

/**
 * FilterRow component for NavContainer
 * Use this component to consistently style filter rows within the NavContainer
 *
 * **IMPORTANT**: Changes to the height of this component (padding, margins, content)
 * may require updating the KNOWN_COLLAPSED_FILTER_HEIGHT
 * constant in `src/hooks/useDynamicHeaderMargin.ts`.
 */
const FilterRow = forwardRef<HTMLDivElement, FilterRowProps>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames(
          'flex w-full flex-wrap items-center bg-primary-container shadow-sm transition-all duration-300 ease-in-out lg:h-[62px] lg:flex-nowrap lg:bg-white lg:pl-6',
          className
        )}
      >
        {children}
      </div>
    );
  }
);

FilterRow.displayName = 'FilterRow';

// Attach the FilterRow component to NavContainer, similar to SideLayout pattern
NavContainer.FilterRow = FilterRow;
